import { pxToRem, SiteVariablesPrepared } from '@fluentui/react-northstar';
import { IStackStyles, Stack } from '@fluentui/react/lib/Stack';
import { Customizer } from '@fluentui/react/lib/Utilities';
import * as React from 'react';
import { useMemo } from 'react';
import { getTeamsTheme } from './teams-customizations';
import {
  darkVariables,
  defaultVariables,
  highContrastVariables
} from './teams-theme-site-variables';
import { ThemeNames } from './teams-theme-types';

export const FabricTeamsThemeCustomizer: React.FC<{
  themeName: ThemeNames;
}> = ({ children, themeName }) => {
  const customizations = useMemo(() => getTeamsTheme(themeName), [themeName]);

  const siteVariables = useMemo((): SiteVariablesPrepared => {
    switch (themeName) {
      case ThemeNames.contrast:
        return highContrastVariables;
      case ThemeNames.dark:
        return darkVariables;
      default:
        return defaultVariables;
    }
  }, [themeName]);

  const scrollVariables = useMemo(() => {
    switch (themeName) {
      case 'dark': {
        // from @fluentui/react/src/themes/teams-dark/components/Provider/providerBoxVariables.ts
        return {
          scrollbarThumbBackgroundColor: siteVariables.colors.grey[450],
          scrollbarThumbHoverBackgroundColor: siteVariables.colors.grey[400]
        };
      }
      case 'contrast': {
        // from @fluentui/react/src/themes/teams-high-contrast/components/Provider/providerBoxVariables.ts
        return {
          scrollbarThumbBackgroundColor: siteVariables.colors.white,
          scrollbarThumbHoverBackgroundColor: siteVariables.colors.white
        };
      }
      default: {
        // from @fluentui/react/src/themes/teams/components/Provider/providerBoxVariables.ts
        return {
          scrollbarThumbBackgroundColor: siteVariables.colors.grey[250],
          scrollbarThumbHoverBackgroundColor: siteVariables.colors.grey[350]
        };
      }
    }
  }, [siteVariables.colors.grey, siteVariables.colors.white, themeName]);

  // styles from fluentui/react/src/themes/teams/components/Provider/providerBoxStyles.ts
  const scrollbarStyles = useMemo(() => {
    const styles: Partial<IStackStyles> = {
      root: {
        selectors: {
          ':global(*::-webkit-scrollbar)': {
            height: pxToRem(16),
            width: pxToRem(16)
          },
          ':global(*::-webkit-scrollbar:disabled)': {
            display: 'none'
          },
          ':global(*::-webkit-scrollbar-thumb)': {
            borderRadius: pxToRem(9),
            border: `solid ${pxToRem(4)} transparent`,
            backgroundClip: 'content-box',
            backgroundColor: scrollVariables.scrollbarThumbBackgroundColor
          },
          ':global(*::-webkit-scrollbar-thumb:hover)': {
            backgroundColor: scrollVariables.scrollbarThumbHoverBackgroundColor,
            border: `solid ${pxToRem(2)} transparent`
          },
          ':global(*::-webkit-scrollbar-track)': {
            background: 'transparent'
          }
        }
      }
    };

    return styles;
  }, [scrollVariables]);

  return (
    <Customizer {...customizations}>
      <Stack styles={scrollbarStyles}>{children}</Stack>
    </Customizer>
  );
};
